<template>
  <div class="smf pb-2">
    <div class="mb-2" v-if="!loading">
      <FormulateForm
        ref="form"
        debounce
        :schema="schema"
        v-model="modelProxy"
        @submit="onSubmit"
        @created="onFormReady"
        @failed-validation="onValidate"
      />
    </div>

    <div class="text-center" v-if="loading">
      <b-spinner label="Spinning" variant="primary"></b-spinner>
    </div>
  </div>
</template>

<script>
import axios from "@axios";
import { BSpinner } from "bootstrap-vue";
import { getUserData } from "@/auth/utils";
import SectionGroup from "@/components/BuildRiskForm/SectionGroup.vue";
import FileUploadWithPreview from "@/components/BuildRiskForm/FileUploadWithPreview.vue";
import ReviewCommentsWithPreview from "@/components/BuildRiskForm/ReviewCommentsWithPreview.vue";
import DurationHmsInput from "@/components/BuildRiskForm/DurationHmsInput.vue";
import UserPickerInput from "@/components/BuildRiskForm/UserPickerInput.vue";
import UnitPickerInput from "@/components/BuildRiskForm/UnitPickerInput.vue";
import SimulacrumFormNormalizer from "./SimulacrumFormNormalizer.js";

export default {
  name: "BuildSimulacrumForm",
  components: {
    BSpinner,
    SectionGroup,
    FileUploadWithPreview,
    ReviewCommentsWithPreview,
    DurationHmsInput,
    UserPickerInput,
    UnitPickerInput,
  },
  props: {
    templateId: { type: [Number, String], default: null },
    templateSnapshot: { type: Array, default: () => [] },
    templateSourceIds: { type: Array, default: () => [] },
    simulacrumId: { type: [Number, String], required: true },
    templatesEndpoint: { type: String, default: "/v1/simulacrum_templates" },
    formAnswersEndpoint: { type: String, default: "/v1/simulacrum_form_answers" },
    formByEntityEndpointTemplate: {
      type: String,
      default: "/v1/simulacrum_form_answers/get-form-by-simulacrum/{id}",
    },
    entityIdFieldName: { type: String, default: "id_simulacrum" },
    data: { type: [Array, Object], default: () => ({}) },
    currentState: { type: String, default: "in_progress" },
    isReviewer: { type: Boolean, default: false },
    canEdit: { type: Boolean, default: true },
    validateRequiredOnly: { type: Boolean, default: false },
  },
  data() {
    return {
      // base state
      loading: true,
      templateData: [],
      localTemplateId: null,
      localSimulacrumId: null,

      // form schema and model
      schema: [],
      model: {},
      formId: null,
      idTenant: null,

      // files collected from the custom component
      filesData: {},
      uploadingFieldLabel: null,
      deletingFieldLabel: null,

      // normalizer + sync flags
      _normalizer: null,
      _pendingSnapshot: null,
      _syncingSchema: false,

      // autosave
      autoSaveEnabled: true, // can be toggled
      asDebounceMs: 3000, // save 3s after last change
      asMaxIntervalMs: 20000, // force save every 20s while editing
      _autoDirty: false, // there are unsaved changes
      _asSaving: false, // autosave in-flight
      _asLastHash: "", // last saved visible payload hash
      _asDebounce: null, // debounce timer id
      _asMaxTimer: null, // max interval timer id
    };
  },
  async mounted() {
    // set local id and load initial data
    this.localSimulacrumId = this.simulacrumId || null;
    await this.reloadForm();
  },
  beforeDestroy() {
    // clear timers on component destroy
    this._clearAutoTimers();
  },
  watch: {
    model: {
      deep: true,
      handler() {
        if (this._syncingSchema || this.loading) return;
        this.rebuildDynamicSchema();
        this._onModelTouched();
        this.$emit("model-changed");
      },
    },
    currentState(n) {
      // update normalizer state and rebuild
      if (!this._normalizer) return;
      this._normalizer.currentState = n || "in_progress";
      this.rebuildDynamicSchema();
      // stop autosave only when locked (final / bloqueado)
      if (this._isLocked()) this._clearAutoTimers();
    },
    isReviewer(n) {
      // keep reviewer flag in sync with normalizer
      if (!this._normalizer) return;
      this._normalizer.isReviewer = !!n;
      this.rebuildDynamicSchema();
    },
    canEdit(n) {
      if (!this._normalizer) return;
      const val = typeof n === "boolean" ? n : true;
      this._normalizer.canEdit = val;
      this._normalizer.options.canEdit = val;
      this.rebuildDynamicSchema();
      if (this._isLocked()) this._clearAutoTimers();
    },
    uploadingFieldLabel() {
      this.rebuildDynamicSchema();
    },
    deletingFieldLabel() {
      this.rebuildDynamicSchema();
    },
  },
  computed: {
    // safe v-model for Formulate (never pass arrays)
    modelProxy: {
      get() {
        return this._ensureObj(this.model);
      },
      set(v) {
        this.model = this._ensureObj(v);
      },
    },
  },
  methods: {
    normalizeEndpoint(basePath) {
      return String(basePath || "").replace(/\/+$/, "");
    },
    buildFormAnswersByEntityEndpoint() {
      return String(this.formByEntityEndpointTemplate || "").replace(
        "{id}",
        String(this.localSimulacrumId)
      );
    },
    buildTemplateByIdEndpoint(templateId) {
      return `${this.normalizeEndpoint(this.templatesEndpoint)}/${templateId}`;
    },
    buildFormAnswersEndpoint(formId = null) {
      const base = this.normalizeEndpoint(this.formAnswersEndpoint);
      return formId ? `${base}/${formId}` : base;
    },
    // plain objects
    _ensureObj(v) {
      return v && typeof v === "object" && !Array.isArray(v) ? v : {};
    },

    // simple slug helper
    slugify(s) {
      return String(s || "")
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/\s+/g, "-")
        .replace(/-+/g, "-");
    },

    _normalizeReviewComments() {
      if (!this.model || typeof this.model !== "object") return;

      const keys = Object.keys(this.model);
      keys.forEach((key) => {
        const isReviewCommentsKey = key.startsWith("review_comments_") || 
                                    key.startsWith("review comments ");
        if (!isReviewCommentsKey) return;

        const value = this.model[key];
        if (!Array.isArray(value)) return;

        const normalizedKey = key.startsWith("review comments ") 
          ? key.replace("review comments ", "review_comments_")
          : key;

        if (normalizedKey !== key) {
          this.$set(this.model, normalizedKey, value);
          this.$delete(this.model, key);
        }

        if (value.length === 0) {
          this.$set(this.model, normalizedKey, []);
          return;
        }

        const firstItem = value[0];
        const isFileArray = firstItem && 
                           typeof firstItem === "object" && 
                           (firstItem.url || firstItem.name) &&
                           !firstItem.text && 
                           !firstItem.user && 
                           !firstItem.date &&
                           !Array.isArray(firstItem.files);

        if (isFileArray) {
          const normalizedComments = [{
            text: "",
            date: new Date().toISOString(),
            user: "",
            files: value
              .map((file) => ({
                url: file.url || "",
                name: file.name || file.url?.split("/").pop() || "",
              }))
              .filter((f) => f.url && String(f.url).trim() !== ""),
          }];
          this.$set(this.model, normalizedKey, normalizedComments);
          return;
        }

        const normalizedComments = value.map((item) => {
          if (!item || typeof item !== "object") {
            return {
              text: "",
              date: new Date().toISOString(),
              user: "",
              files: [],
            };
          }

          const normalizedItem = {
            text: item.text || "",
            date: item.date || new Date().toISOString(),
            user: item.user || "",
            files: [],
          };

          if (item.files) {
            if (Array.isArray(item.files)) {
              normalizedItem.files = item.files
                .map((f) => {
                  if (typeof f === "string") {
                    return { url: f, name: f.split("/").pop() || f };
                  }
                  if (f && typeof f === "object") {
                    return {
                      url: f.url || "",
                      name: f.name || f.url?.split("/").pop() || "",
                    };
                  }
                  return null;
                })
                .filter((f) => f && f.url && String(f.url).trim() !== "");
            }
          } else if (item.url || item.name) {
            normalizedItem.files = [{
              url: item.url || "",
              name: item.name || item.url?.split("/").pop() || "",
            }].filter((f) => f.url && String(f.url).trim() !== "");
          }

          return normalizedItem;
        });

        this.$set(this.model, normalizedKey, normalizedComments);
      });
    },

    // build table of contents for anchors
    buildToc() {
      if (!this._normalizer) return [];
      const processed = this._normalizer.getProcessedFields();
      const { order, structured } = this._normalizer.groupBySectionSubsection(processed);
      return order.map((secName) => {
        const entry = structured.get(secName) || { subOrder: [] };
        return {
          id: `sec-${this.slugify(secName)}`,
          label: secName,
          children: (entry.subOrder || []).map((sub) => ({
            id: `sub-${this.slugify(secName)}-${this.slugify(sub)}`,
            label: sub,
          })),
        };
      });
    },

    // emit toc to parent
    emitToc() {
      this.$emit("toc", this.buildToc());
    },

    // form hooks (kept for possible future use)
    onFormReady() {},
    onValidate() {},

    // asks if an answer row already exists for the simulacrum
    async checkIfFormExists() {
      return axios.get(this.buildFormAnswersByEntityEndpoint());
    },

    // loads data (either from answer or from template/snapshot)
    async reloadForm() {
      this.loading = true;
      this.localTemplateId = this.templateId;

      try {
        if (!this.localSimulacrumId && this.simulacrumId)
          this.localSimulacrumId = this.simulacrumId;

        if (!this.localSimulacrumId) {
          // when no simulacrum id, use given snapshot or empty
          await this.fetchTemplateOnlyOrSnapshot();
        } else {
          // try to load existing answers
          try {
            const resp = await this.checkIfFormExists();
            const formData = resp.data.data;

            this.formId = formData.id || null;
            this.model = this._ensureObj(formData.data || {});
            this._normalizeReviewComments();
            this.localTemplateId = formData.id_template || this.localTemplateId;

            // pick snapshot first, fallback to template.data
            this.templateData =
              Array.isArray(formData.template_snapshot) &&
              formData.template_snapshot.length
                ? formData.template_snapshot
                : formData.template && Array.isArray(formData.template.data)
                ? formData.template.data
                : [];

            if (
              (!Array.isArray(this.templateData) || !this.templateData.length) &&
              Array.isArray(this.templateSnapshot) &&
              this.templateSnapshot.length
            ) {
              this.templateData = this.templateSnapshot;
            }

            this.idTenant = formData.template?.id_tenant || null;
          } catch (_) {
            // not found, fall back to template or snapshot
            this.formId = null;
            await this.fetchTemplateOnlyOrSnapshot();
          }
        }

        // init normalizer and schema
        this.initializeFormNormalizer();
        this.rebuildDynamicSchema();

        // Clean filesData and sync with backend files after reload
        this._syncFilesDataFromModel();

        // set baseline hash to avoid triggering autosave on open
        const baseline = this._normalizer
          ? this._normalizer.pruneModelInvisible(this.model || {})
          : this.model || {};
        this._asLastHash = this._hash(baseline);
        this._autoDirty = false;
        this._clearAutoTimers();
      } finally {
        this.loading = false;
      }
    },

    // loads only template (or provided snapshot)
    async fetchTemplateOnlyOrSnapshot() {
      if (this.templateSnapshot && this.templateSnapshot.length) {
        // use snapshot as transient template
        this.templateData = this.templateSnapshot;
        this.idTenant = null;
        this.localTemplateId = null;
        this.model = {};
        return;
      }
      if (!this.localTemplateId) {
        // no template id and no snapshot, start empty
        this.templateData = [];
        this.model = {};
        return;
      }
      // fetch template by id
      const { data } = await axios.get(this.buildTemplateByIdEndpoint(this.localTemplateId));
      this.templateData = data.data || data;
      this.idTenant = data.id_tenant || null;
      this.model = {};
    },

    // instantiates normalizer and computes initial schema
    initializeFormNormalizer() {
      this._normalizer = new SimulacrumFormNormalizer(this, this.templateData, {
        disabled: false,
        currentState: this.currentState,
        isReviewer: this.isReviewer === true,
        canEdit: this.canEdit,
        model: this.model,
        customComponents: {
          SectionGroup,
          FileUploadWithPreview,
          ReviewCommentsWithPreview,
          DurationHmsInput,
          UserPickerInput,
          UnitPickerInput,
        },
        onUpdateFiles: this.handleFileUpload,
        onUpdateComments: ({ label, comments }) => {
          this.$set(this.model, label, comments || []);
        },
        onAddComment: this.addCommentAndSave,
        uploadingFieldLabel: this.uploadingFieldLabel,
        deletingFieldLabel: this.deletingFieldLabel,
      });
      this.schema = this._normalizer.convert();
      this.$nextTick(() => this.emitToc());
    },

    // rebuild schema to reflect visibility changes and prune model
    rebuildDynamicSchema() {
      if (!this._normalizer) return;
      this._syncingSchema = true;
      if (this._normalizer.options) {
        this._normalizer.options.uploadingFieldLabel = this.uploadingFieldLabel;
        this._normalizer.options.deletingFieldLabel = this.deletingFieldLabel;
      }
      try {
        this._normalizer.model = this.model || {};
        const pruned = this._normalizer.pruneModelInvisible(this.model || {});
        if (JSON.stringify(pruned) !== JSON.stringify(this.model || {}))
          this.model = pruned;
        this.schema = this._normalizer.convert();
      } finally {
        this.$nextTick(() => {
          this._syncingSchema = false;
          this.emitToc();
        });
      }
    },

    ensureSimulacrumId() {
      if (!this.localSimulacrumId) {
        this.localSimulacrumId = this.simulacrumId || null;
      }
      return !!this.localSimulacrumId;
    },

    handleFileUpload({ files, label }) {
      const previousLength = (this.filesData[label] || []).length;
      if (!this.filesData[label]) this.$set(this.filesData, label, []);
      this.filesData[label] = (files || []).map((f) => ({
        url: f.url || "",
        name: f.name,
        raw: f.raw || null,
      }));

      const hasRawFiles = (files || []).some((f) => f && f.raw);
      if (hasRawFiles) {
        this.uploadingFieldLabel = label;
        this.uploadFilesForField(label, this.filesData[label]);
        return;
      }

      const newLength = (files || []).length;
      if (newLength < previousLength) {
        this.deletingFieldLabel = label;
        this.uploadFilesForField(label, this.filesData[label]);
        return;
      }

      this._onModelTouched();
    },

    async addCommentAndSave({ label, text, files }) {
      if (!this.ensureSimulacrumId()) {
        throw new Error(this.$t?.("risk.missing_evaluation") || "Missing simulacrum id");
      }

      const user = getUserData()?.email || "anonymous@domain";
      const newComment = {
        text: text || "",
        date: new Date().toISOString(),
        user,
        files: (files || []).map((file) => ({
          name: file.name,
          url: "",
          raw: file,
        })),
      };

      const existingComments = Array.isArray(this.model[label]) ? this.model[label] : [];
      const updatedComments = [...existingComments, newComment];
      this.$set(this.model, label, updatedComments);

      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(this.model || {})
        : { ...this.model };
      const finalData = { ...baseline };

      const visibleFileLabels = this._normalizer
        ? this._normalizer.visibleFileLabels(baseline)
        : new Set();
      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;
        const allFiles = this.filesData[fieldName] || [];
        finalData[fieldName] = allFiles
          .filter(f => f.url && String(f.url).trim() !== "")
          .map((f) => ({ url: f.url || "", name: f.name }));
      }

      const reviewKeys = Object.keys(this.model || {}).filter((k) =>
        k.startsWith("review_comments_")
      );
      reviewKeys.forEach((rk) => {
        const commentsArr = Array.isArray(this.model[rk]) ? this.model[rk] : [];
        finalData[rk] = commentsArr.map((c) => ({
          text: c.text,
          date: c.date,
          user: c.user,
          files: Array.isArray(c.files)
            ? c.files.map((f) => ({
                url: (f.url && !String(f.url).startsWith("blob:")) ? f.url : "",
                name: f.name,
              }))
            : [],
        }));
      });

      const formData = new FormData();
      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;
        const allFiles = this.filesData[fieldName] || [];
        allFiles.forEach((f) => {
          if (f.raw) formData.append(`${fieldName}[]`, f.raw);
        });
      }
      reviewKeys.forEach((rk) => {
        const commentsArr = Array.isArray(this.model[rk]) ? this.model[rk] : [];
        commentsArr.forEach((comment, cIdx) => {
          const commentFiles = Array.isArray(comment.files) ? comment.files : [];
          commentFiles.forEach((fObj) => {
            const fileToSend = fObj.raw instanceof File || fObj.raw instanceof Blob
              ? fObj.raw
              : null;
            const needsAppend = !fObj.url || String(fObj.url || "").startsWith("blob:");
            if (fileToSend && needsAppend) {
              formData.append(`${rk}[${cIdx}][files][]`, fileToSend);
            }
          });
        });
      });

      formData.append("template_snapshot", JSON.stringify(this.templateData || []));
      formData.append("template_sources", JSON.stringify(this.templateSourceIds || []));
      if (this.localTemplateId) formData.append("id_template", this.localTemplateId);
      if (this.idTenant) formData.append("id_tenant", this.idTenant);
      formData.append(this.entityIdFieldName, this.localSimulacrumId);
      formData.append("data", JSON.stringify(finalData || {}));

      const url = this.buildFormAnswersEndpoint(this.formId);
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.formId ? "PUT" : "POST",
        },
      };

      const response = await axios.post(url, formData, config);
      const payload = response.data?.data ?? response.data;
      if (payload && payload.id) {
        this.formId = payload.id;
      }
      const savedData = payload?.data ?? response.data?.data?.data ?? response.data?.data;
      if (savedData && typeof savedData === "object") {
        reviewKeys.forEach((rk) => {
          if (Array.isArray(savedData[rk])) {
            const savedComments = savedData[rk].map((c) => ({
              text: c.text || "",
              date: c.date || "",
              user: c.user || "",
              files: (c.files || []).map((f) => ({
                url: f.url || "",
                name: f.name || "",
              })),
            }));
            this.$set(this.model, rk, savedComments);
          }
        });
      }
      this._asLastHash = this._hash(baseline);
      this._autoDirty = false;
      this._clearAutoTimers();
      this._clearTemporaryFiles();
      this.rebuildDynamicSchema();
    },

    async uploadFilesForField(label, files) {
      if (!this.ensureSimulacrumId()) {
        this.uploadingFieldLabel = null;
        this.deletingFieldLabel = null;
        this.$toast?.error?.(this.$t?.("risk.missing_evaluation") || "Missing simulacrum id");
        return;
      }

      const formData = this._buildFormDataForField(label, files);
      const url = this.buildFormAnswersEndpoint(this.formId);
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.formId ? "PUT" : "POST",
        },
      };

      try {
        const response = await axios.post(url, formData, config);
        const payload = response.data?.data ?? response.data;
        if (payload && payload.id) {
          this.formId = payload.id;
        }
        const savedData = payload?.data ?? response.data?.data?.data ?? response.data?.data;
        if (savedData && Array.isArray(savedData[label])) {
          const fileList = savedData[label].map((f) => ({
            url: f.url || "",
            name: f.name || "",
            raw: null,
          }));
          this.$set(this.filesData, label, fileList);
          this.$set(this.model, label, fileList);
        }
      } catch (error) {
        this.$toast?.error?.(this.$t?.("risk.save_failed") || "No se pudo guardar");
        this.$emit("submitError", { error });
      } finally {
        this.uploadingFieldLabel = null;
        this.deletingFieldLabel = null;
      }
    },

    _buildFormDataForField(fieldName, allFiles) {
      const formData = new FormData();
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(this.model || {})
        : { ...this.model };
      const fileListForData = (allFiles || []).map((f) => ({
        url: f.raw ? "" : (f.url || ""),
        name: f.name || "",
      }));
      const finalData = { ...baseline, [fieldName]: fileListForData };

      for (const file of allFiles || []) {
        if (file.raw) formData.append(`${fieldName}[]`, file.raw);
      }

      formData.append("template_snapshot", JSON.stringify(this.templateData || []));
      formData.append("template_sources", JSON.stringify(this.templateSourceIds || []));
      if (this.localTemplateId) formData.append("id_template", this.localTemplateId);
      if (this.idTenant) formData.append("id_tenant", this.idTenant);
      formData.append(this.entityIdFieldName, this.localSimulacrumId);
      formData.append("data", JSON.stringify(finalData || {}));
      return formData;
    },

    // Clear temporary files (with raw or blob URLs)
    _clearTemporaryFiles() {
      for (const fieldName in this.filesData) {
        const files = this.filesData[fieldName] || [];
        // Remove files that have raw (temporary) or blob URLs
        const cleanedFiles = files.filter(f => {
          if (f.raw) return false; // Remove files with raw
          if (f.url && f.url.startsWith('blob:')) return false; // Remove blob URLs
          return true; // Keep files with backend URLs
        });
        if (cleanedFiles.length !== files.length) {
          this.$set(this.filesData, fieldName, cleanedFiles);
        }
      }
    },

    // Sync filesData with model files from backend (clean temporary files)
    _syncFilesDataFromModel() {
      if (!this.model || !this.templateData) return;

      // Find all file fields in template
      const fileFields = [];
      const findFileFields = (nodes) => {
        (nodes || []).forEach((node) => {
          if (node.type === 'files_section' && node.name) {
            fileFields.push(node.name);
          }
          if (Array.isArray(node.children)) {
            findFileFields(node.children);
          }
        });
      };
      findFileFields(this.templateData);

      // Clear filesData and sync with model
      fileFields.forEach((fieldName) => {
        const modelFiles = Array.isArray(this.model[fieldName]) ? this.model[fieldName] : [];
        // Only keep files that have a URL from backend (not temporary blob URLs)
        const backendFiles = modelFiles.filter(f => f.url && String(f.url).trim() !== "" && !f.url.startsWith('blob:'));
        this.$set(this.filesData, fieldName, backendFiles.map(f => ({
          url: f.url || "",
          name: f.name || "",
          raw: null,
        })));
      });
    },

    // helper to fetch and convert url to Blob for re-upload
    async convertUrlToBlob(url) {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`Failed ${url}: ${res.statusText}`);
        return await res.blob();
      } catch (e) {
        console.error("blob error", e);
        return null;
      }
    },

    // manual submit by button or parent
    submitForm() {
      try {
        this._pendingSnapshot = JSON.parse(JSON.stringify(this.model || {}));
      } catch (_) {
        this._pendingSnapshot = { ...(this.model || {}) };
      }

      const formRef = this.$refs?.form || null;
      if (formRef && typeof formRef.submit === "function") formRef.submit();
      else this.onSubmit();
    },

    // submit handler (opts.autosave true avoids blocking overlay)
    async onSubmit(values, opts = {}) {
      const isAutosave = opts.autosave === true;

      if (!isAutosave) this.loading = true;
      if (!this.localSimulacrumId) {
        if (!isAutosave) this.loading = false;
        return;
      }

      // pick freshest model snapshot
      const isEvent = values && typeof values.preventDefault === "function";
      const fresh =
        this._pendingSnapshot ||
        (!isEvent && values) ||
        this.$refs?.form?.context?.model ||
        this.model;

      const cleanedFresh = this._cleanEmptyValues(fresh);
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(cleanedFresh)
        : cleanedFresh;

      const finalData = { ...baseline };

      // multipart for files
      const formData = new FormData();

      // include only visible files sections
      const visibleFileLabels = this._normalizer
        ? this._normalizer.visibleFileLabels(baseline)
        : new Set();
      for (const fieldName in this.filesData) {
        if (fieldName.startsWith("review_comments_")) continue;
        if (visibleFileLabels.size && !visibleFileLabels.has(fieldName)) continue;

        const allFiles = this.filesData[fieldName] || [];
        finalData[fieldName] = allFiles
          .filter(f => f.url && String(f.url).trim() !== "")
          .map((f) => ({
            url: f.url || "",
            name: f.name,
          }));

        for (const f of allFiles) {
          if (f.raw) {
            formData.append(`${fieldName}[]`, f.raw);
          }
        }
      }

      const reviewKeys = Object.keys(this.model || {}).filter((k) =>
        k.startsWith("review_comments_")
      );
      reviewKeys.forEach((rk) => {
        const commentsArr = Array.isArray(this.model[rk]) ? this.model[rk] : [];
        finalData[rk] = commentsArr.map((c) => ({
          text: c.text,
          date: c.date,
          user: c.user,
          files: Array.isArray(c.files)
            ? c.files.map((f) => ({
                url: (f.url && !String(f.url).startsWith("blob:")) ? f.url : "",
                name: f.name,
              }))
            : [],
        }));
        commentsArr.forEach((comment, cIdx) => {
          const files = Array.isArray(comment.files) ? comment.files : [];
          files.forEach((fObj) => {
            let fileToSend = null;
            if (fObj.raw instanceof File || fObj.raw instanceof Blob) {
              fileToSend = fObj.raw;
            } else if (fObj instanceof File || fObj instanceof Blob) {
              fileToSend = fObj;
            } else if (fObj.file && (fObj.file instanceof File || fObj.file instanceof Blob)) {
              fileToSend = fObj.file;
            } else if (fObj.originFileObj && (fObj.originFileObj instanceof File || fObj.originFileObj instanceof Blob)) {
              fileToSend = fObj.originFileObj;
            }
            const needsAppend = !fObj.url || fObj.url.trim() === "" || String(fObj.url || "").startsWith("blob:");
            if (fileToSend && needsAppend) {
              formData.append(`${rk}[${cIdx}][files][]`, fileToSend);
            }
          });
        });
      });

      formData.append("template_snapshot", JSON.stringify(this.templateData || []));
      formData.append("template_sources", JSON.stringify(this.templateSourceIds || []));
      if (this.localTemplateId) formData.append("id_template", this.localTemplateId);
      if (this.idTenant) formData.append("id_tenant", this.idTenant);
      formData.append(this.entityIdFieldName, this.localSimulacrumId);
      formData.append("data", JSON.stringify(finalData || {}));

      // method override to support create/update with same endpoint
      const config = {
        headers: {
          "Content-Type": "multipart/form-data",
          "X-HTTP-Method-Override": this.formId ? "PUT" : "POST",
        },
      };

      try {
        // choose endpoint by presence of formId
        const url = this.buildFormAnswersEndpoint(this.formId);
        await axios.post(url, formData, config);

        // mark last saved hash and clear timers after success
        this._asLastHash = this._hash(baseline);
        this._autoDirty = false;
        this._clearAutoTimers();

        // fetch formId after first save or refresh data on manual save
        if (!this.formId) {
          // Clear temporary files before reload
          this._clearTemporaryFiles();
          await this.reloadForm();
        } else if (!isAutosave) {
          // Clear temporary files before reload
          this._clearTemporaryFiles();
          await this.reloadForm();
        }

        // bubble success events
        this.$emit("submit");
        this.$emit("submit-success");
      } catch (error) {
        // bubble error events
        this.$emit("submitError", { error });
        if (isAutosave) this.$emit("auto-save-error", error);
      } finally {
        // stop overlay for manual saves
        if (!isAutosave) this.loading = false;
        // clear temp flags
        this._pendingSnapshot = null;
        this._syncingSchema = false;
      }
    },

    // ===== AUTOSAVE utils (uses isFormLocked from normalizer) =====
    _isLocked() {
      if (!this._normalizer) return true;
      return this._normalizer.isFormLocked();
    },

    // autosave: simple hash based on visible payload
    _hash(v) {
      try {
        return JSON.stringify(v);
      } catch (_) {
        return String(Date.now());
      }
    },

    _onModelTouched() {
      if (this.uploadingFieldLabel != null || this.deletingFieldLabel != null) return;
      if (!this.autoSaveEnabled || this.loading || this._isLocked()) return;
      this._autoDirty = true;

      // debounce save after inactivity
      clearTimeout(this._asDebounce);
      this._asDebounce = setTimeout(
        () => this._autoSaveNow("debounce"),
        this.asDebounceMs
      );

      // force save periodically while editing
      if (!this._asMaxTimer) {
        this._asMaxTimer = setTimeout(
          () => this._autoSaveNow("max"),
          this.asMaxIntervalMs
        );
      }
    },

    // autosave: clear timers
    _clearAutoTimers() {
      clearTimeout(this._asDebounce);
      this._asDebounce = null;
      clearTimeout(this._asMaxTimer);
      this._asMaxTimer = null;
    },

    // autosave: perform save if needed
    async _autoSaveNow(reason = "debounce") {
      if (this.uploadingFieldLabel != null || this.deletingFieldLabel != null) return;
      if (!this._autoDirty || this._asSaving || this.loading || this._isLocked()) return;

      // take a snapshot for submit
      try {
        this._pendingSnapshot = JSON.parse(JSON.stringify(this.model || {}));
      } catch (_) {
        this._pendingSnapshot = { ...(this.model || {}) };
      }

      // skip if visible payload did not change
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(this._pendingSnapshot)
        : this._pendingSnapshot;
      const h = this._hash(baseline);
      if (h === this._asLastHash) {
        this._autoDirty = false;
        this._clearAutoTimers();
        return;
      }

      // flag saving and fire submit in autosave mode
      this._asSaving = true;
      this.$emit("auto-save-start", { reason });

      try {
        await this.onSubmit(undefined, { autosave: true });
        this.$emit("auto-save-done", { reason });
      } catch (e) {
        // errors already bubbled by onSubmit
      } finally {
        this._asSaving = false;
        this._clearAutoTimers();
        if (this._autoDirty) {
          this._asDebounce = setTimeout(
            () => this._autoSaveNow("debounce"),
            this.asDebounceMs
          );
        }
      }
    },

    _cleanEmptyValues(model) {
      if (!model || typeof model !== "object") return {};
      const cleaned = {};
      for (const key in model) {
        if (!model.hasOwnProperty(key)) continue;
        const value = model[key];
        if (value === null || value === undefined) continue;
        if (typeof value === "string" && value.trim() === "") continue;
        if (Array.isArray(value) && value.length === 0) continue;
        cleaned[key] = value;
      }
      return cleaned;
    },

    isFormValid() {
      if (!this._normalizer) return true;

      const currentModel = this._cleanEmptyValues(this.model || {});
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(currentModel)
        : currentModel;

      const processed = this._normalizer.getProcessedFields() || [];
      const { order, groups } = this._normalizer.groupBySection(processed);

      for (const secName of order) {
        const sec = groups.get(secName) || { fields: [] };
        const secFields = sec.fields || [];
        const indexMap = new Map(secFields.map((f, idx) => [f.item_id, idx]));

        for (const f of secFields) {
          if (f.type === "section_header" || f.type === "subsection_header") continue;

          const idx = indexMap.has(f.item_id) ? indexMap.get(f.item_id) : 0;
          if (!this._mustValidateField(f)) continue;
          const isVisible = this._normalizer.isFieldVisible(f, secFields, idx, baseline);
          if (!isVisible) continue;

          const filled = this._isAnswerFilled(f, baseline);
          if (!filled) return false;
        }
      }

      return true;
    },

    async validateBeforeReview() {
      if (!this._normalizer) return { ok: true, missing: [] };

      const currentModel = this._cleanEmptyValues(this.model || {});
      const baseline = this._normalizer
        ? this._normalizer.pruneModelInvisible(currentModel)
        : currentModel;

      const missing = [];

      const processed = this._normalizer.getProcessedFields() || [];
      const { order, groups } = this._normalizer.groupBySection(processed);

      order.forEach((secName) => {
        const sec = groups.get(secName) || { fields: [] };
        const secFields = sec.fields || [];

        const indexMap = new Map(secFields.map((f, idx) => [f.item_id, idx]));

        secFields.forEach((f) => {
          if (f.type === "section_header" || f.type === "subsection_header") return;

          const idx = indexMap.has(f.item_id) ? indexMap.get(f.item_id) : 0;
          if (!this._mustValidateField(f)) return;
          const isVisible = this._normalizer.isFieldVisible(f, secFields, idx, baseline);
          if (!isVisible) return;

          const filled = this._isAnswerFilled(f, baseline);
          if (!filled) {
            missing.push({
              name: f.name,
              section: f.section || secName || "",
              subsection: f.subsection || null,
            });
          }
        });
      });

      if (missing.length) {
        return { ok: false, missing };
      }
      return { ok: true, missing: [] };
    },

    _mustValidateField(field) {
      if (!this.validateRequiredOnly) return true;
      return !!field?.options?.required;
    },

    // checks value presence by field type
    _isAnswerFilled(field, answers) {
      const t = String(field.type || "");
      const v = answers[field.name];

      // helper that treats 0 as valid
      const hasValue = (x) => !(x === undefined || x === null || String(x) === "");

      // duration_hm creates two sub-keys
      if (t === "duration_hm") {
        const h = answers[`${field.name} - horas`];
        const m = answers[`${field.name} - minutos`];
        return hasValue(h) && hasValue(m);
      }

      // duration_ms creates two sub-keys
      if (t === "duration_ms") {
        const m = answers[`${field.name} - minutos`];
        const s = answers[`${field.name} - segundos`];
        return hasValue(m) && hasValue(s);
      }

      // duration_hms creates an object with hours, minutes, seconds
      if (t === "duration_hms") {
        const val = answers[field.name];
        if (!val || typeof val !== "object") return false;
        const hours = Number(val.hours) || 0;
        const minutes = Number(val.minutes) || 0;
        const seconds = Number(val.seconds) || 0;
        return hours > 0 || minutes > 0 || seconds > 0;
      }

      // checkbox requires at least one checked
      if (t === "checkbox") {
        return Array.isArray(v) && v.length > 0;
      }

      // files_section is not required, can be empty
      if (t === "files_section") {
        return true;
      }

      // select/radio/text/textarea/number/time/minutes/numeric -> value must exist
      return hasValue(v);
    },
  },
};
</script>

<style scoped>
.smf .duration-hm-row {
  display: grid;
  grid-template-columns: repeat(2, minmax(160px, 1fr));
  gap: 12px;
  align-items: end;
}
.smf .field-divider {
  margin: 16px 0;
  border: 0;
  border-top: 1px solid #edf0f3;
}
</style>
